import cn from 'classnames';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSocket } from 'sonata-platform-components';
import { useQueryFetch } from 'sonata-react-components';

import { getPlatform, updatePlatformReloadForUser } from '../../../services/platform.service';

import './updateBar.scss';

import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';

type Props = {
  user: UserInterface;
};

type State = {
  secondsLeft: number | null;
  isExpanded: boolean;
};

type SocketNotification = {
  section: string;
  status: {
    active: boolean;
    modified: string;
    seconds: number;
  };
};

export function UpdateBar({ user }: Readonly<Props>) {
  const componentId = 'MultiflightMenuUpdateBar';
  const { formatMessage } = useIntl();
  const { offEvent, onEvent } = useSocket();
  const [{ secondsLeft, isExpanded }, setState] = useState<State>({ isExpanded: false, secondsLeft: null });

  const applyDiffInSeconds = (modified: string, seconds: number): void => {
    const now = moment.utc();
    const updateTime = moment.utc(modified).add(seconds, 'seconds');
    const diffInSeconds = updateTime.diff(now, 'seconds');
    if (diffInSeconds > 0) {
      setState(prev => ({
        ...prev,
        secondsLeft: diffInSeconds
      }));
    }
  };

  const tokens = {
    getPlatformConfig: {},
    updatePlatformReload: {}
  };

  const { data: platformConfig } = useQueryFetch(
    ['platformConfig', user],
    async () => getPlatform(tokens.getPlatformConfig),
    {
      cacheTime: 10 * 60 * 1000
    }
  );

  if (platformConfig?.update?.active && !user.platformUpdated && secondsLeft === null) {
    const updateConfig = platformConfig?.update;
    applyDiffInSeconds(updateConfig.modified, updateConfig.seconds);
  }

  const handleWebSocketMessage = (notification: SocketNotification) => {
    if (notification.section !== 'update') {
      return;
    }
    if (!notification.status.active) {
      return;
    }
    applyDiffInSeconds(notification.status.modified, notification.status.seconds);
  };

  useEffect(() => {
    onEvent(componentId, 'platform', handleWebSocketMessage);

    return () => {
      offEvent(componentId);
    };
  }, []);

  useEffect(() => {
    if (secondsLeft === null) {
      return;
    }
    if (secondsLeft <= 0) {
      reloadPage();
    }
    const timer = setInterval(() => {
      setState(prevState => ({ ...prevState, secondsLeft: (prevState.secondsLeft as number) - 1 }));
    }, 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const reloadPage = async () => {
    await updatePlatformReloadForUser((user._id || user.id) as string, tokens.updatePlatformReload);
    setState(prev => ({
      ...prev,
      secondsLeft: null
    }));
    const cacheBustUrl = `${window.location.origin}?cachebust=${Date.now()}`;
    if (typeof window.location?.reload === 'function') {
      window.location.href = cacheBustUrl;
    } else {
      window.location.href = cacheBustUrl;
    }
  };

  const formatTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return [hours, minutes, seconds].map(v => v.toString().padStart(2, '0')).join(':');
  };

  const handleHover = (isHovered: boolean) => {
    setState(prevState => ({
      ...prevState,
      isExpanded: isHovered
    }));
  };

  if (!secondsLeft) {
    return null;
  }

  return (
    <div className={cn('updateBar', { 'updateBar--expanded': isExpanded })}>
      <div className="updateBar__info" onMouseEnter={() => handleHover(true)} onMouseLeave={() => handleHover(false)}>
        <span className="updateBar__icon fa-regular fa-timer" />
        {isExpanded && (
          <span className="updateBar__label">
            {formatMessage({
              defaultMessage: 'Autoupdate',
              id: 'menu.updateBar.autoupdate'
            })}
          </span>
        )}
        <span className="updateBar__countdown">{formatTime(secondsLeft ?? 0)}</span>
        {isExpanded && (
          <span className="updateBar__description">
            {formatMessage({
              defaultMessage:
                'We have made a few updates to make the platform faster, more reliable, and easier to use.',
              id: 'menu.updateBar.description'
            })}
          </span>
        )}
        <button className="updateBar__button" onClick={reloadPage} type="button">
          <span className="updateBar__buttonText">
            {formatMessage({
              defaultMessage: 'Update Now',
              id: 'menu.updateBar.updateNow'
            })}
          </span>
        </button>
      </div>
    </div>
  );
}
